import React, { useEffect, useState, useRef } from "react"
import { Button, Icon } from "@blueprintjs/core"
import classNames from "classnames"
import { useTranslation } from "react-i18next"

type VoiceRecordingBarProps = {
  isRecording: boolean
  isPaused: boolean
  audioStream: MediaStream | null
  recordedAudioUrl: string | null
  onPause: () => void
  onResume: () => void
  onStop: () => void
  onCancel: () => void
  onSend: () => void
  createPreviewForPlayback: () => Promise<string | null>
}

const VoiceRecordingBar: React.FC<VoiceRecordingBarProps> = ({
  isRecording,
  isPaused,
  audioStream,
  recordedAudioUrl,
  onPause,
  onResume,
  onCancel,
  onSend,
  createPreviewForPlayback,
}) => {
  const { t } = useTranslation()
  const [duration, setDuration] = useState(0)
  const [audioLevel, setAudioLevel] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)

  // Timer for recording duration
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isRecording && !isPaused) {
      interval = setInterval(() => {
        setDuration((prev) => prev + 1)
      }, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isRecording, isPaused])

  // Audio level visualization
  useEffect(() => {
    if (!audioStream || isPaused) {
      setAudioLevel(0)
      return
    }

    const audioContext = new AudioContext()
    const analyser = audioContext.createAnalyser()
    const microphone = audioContext.createMediaStreamSource(audioStream)
    const dataArray = new Uint8Array(analyser.frequencyBinCount)

    analyser.smoothingTimeConstant = 0.8
    analyser.fftSize = 1024
    microphone.connect(analyser)

    const updateAudioLevel = () => {
      analyser.getByteFrequencyData(dataArray)
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length
      setAudioLevel(average)

      if (isRecording && !isPaused) {
        requestAnimationFrame(updateAudioLevel)
      }
    }

    updateAudioLevel()

    return () => {
      audioContext.close()
    }
  }, [audioStream, isRecording, isPaused])

  // Reset duration only when component mounts (new recording session)
  useEffect(() => {
    setDuration(0)
  }, [])

  // Handle audio playback
  const handlePlayback = async () => {
    if (!recordedAudioUrl) return

    if (isPlaying) {
      // Stop playback
      if (audioElementRef.current) {
        audioElementRef.current.pause()
        audioElementRef.current.currentTime = 0
      }
      setIsPlaying(false)
    } else {
      // Start playback by creating a fresh preview
      try {
        const previewUrl = await createPreviewForPlayback()
        if (previewUrl && audioElementRef.current) {
          audioElementRef.current.src = previewUrl
          audioElementRef.current.play().catch((error) => {
            console.error("Failed to play audio:", error)
            setIsPlaying(false)
          })
          setIsPlaying(true)
        }
      } catch (error) {
        console.error("Failed to create preview for playback:", error)
      }
    }
  }

  // Create audio element for playback
  useEffect(() => {
    if (!audioElementRef.current) {
      audioElementRef.current = new Audio()
      audioElementRef.current.onended = () => {
        setIsPlaying(false)
      }
    }

    return () => {
      if (audioElementRef.current) {
        audioElementRef.current.pause()
        audioElementRef.current = null
      }
    }
  }, [])

  // Stop playback when recording resumes
  useEffect(() => {
    if (isRecording && isPlaying) {
      if (audioElementRef.current) {
        audioElementRef.current.pause()
        audioElementRef.current.currentTime = 0
      }
      setIsPlaying(false)
    }
  }, [isRecording, isPlaying])

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const getWaveHeight = () => {
    const normalizedLevel = Math.min(audioLevel / 50, 1)
    return Math.max(normalizedLevel * 20, 2)
  }

  return (
    <div className="mb-2 animate-slide-down rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800 md:px-4">
      <div className="flex max-h-6 items-center justify-between">
        <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300 md:gap-3">
          <Icon
            icon="microphone"
            className={classNames("transition-colors", {
              "animate-pulse text-red-600 dark:text-red-500":
                isRecording && !isPaused,
              "text-orange-500 dark:text-orange-400": isPaused,
              "text-red-500 dark:text-red-400": !isPaused && !isRecording,
            })}
          />
          <div className="flex h-5 items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="min-h-0.5 w-0.5 animate-wave rounded-sm bg-gradient-to-t from-red-500 to-pink-400 dark:from-red-600 dark:to-pink-500"
                style={{
                  height: `${getWaveHeight() + Math.random() * 5}px`,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>
          <span className="min-w-10 font-mono text-xs font-medium text-gray-700 dark:text-gray-300 md:text-sm">
            {formatDuration(duration)}
          </span>
          {isPaused && (
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold uppercase text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
              {t("app.voiceRecording.paused")}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-2">
          <Button
            icon="cross"
            minimal
            small
            intent="danger"
            onClick={onCancel}
            title={t("app.voiceRecording.cancel")}
          />

          {isPaused ? (
            <>
              {recordedAudioUrl && (
                <Button
                  icon={isPlaying ? "stop" : "play"}
                  minimal
                  small
                  onClick={handlePlayback}
                  title={
                    isPlaying
                      ? t("app.voiceRecording.stopPlayback")
                      : t("app.voiceRecording.play")
                  }
                />
              )}
              <Button
                icon="record"
                minimal
                small
                onClick={onResume}
                title={t("app.voiceRecording.resume")}
              />
            </>
          ) : (
            <Button
              icon="pause"
              minimal
              small
              onClick={onPause}
              title={t("app.voiceRecording.pause")}
            />
          )}

          <Button
            icon="send-message"
            minimal
            small
            onClick={onSend}
            title={t("app.voiceRecording.send")}
            intent="primary"
          />
        </div>
      </div>
    </div>
  )
}

export default VoiceRecordingBar
